import { useEffect, useState } from "react";
import { useAdminAuth, adminApi, formatApiError } from "../auth";
import { Mail, MessageSquare, CheckCircle2, XCircle, RefreshCw, AlertTriangle, KeyRound, Eye, EyeOff } from "lucide-react";

const AdminSettings = () => {
  const { user } = useAdminAuth();
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Password change state
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        adminApi.get("/notifications/status"),
        adminApi.get("/notifications/log", { params: { limit: 25 } }),
      ]);
      setStatus(s.data);
      setLog(l.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const sendTest = async () => {
    setSending(true); setMsg(""); setErr("");
    try {
      const { data } = await adminApi.post("/notifications/test");
      const sent = data.events.filter((e) => e.status === "sent").length;
      const skipped = data.events.filter((e) => e.status === "skipped").length;
      const failed = data.events.filter((e) => e.status === "failed").length;
      setMsg(`Test fired against order ${data.order_id.slice(0, 8)}… → ${sent} sent · ${skipped} skipped · ${failed} failed.`);
      await loadAll();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSending(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(""); setPwErr("");
    if (pw.next.length < 8) { setPwErr("New password must be at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { setPwErr("New password and confirmation do not match."); return; }
    if (pw.next === pw.current) { setPwErr("New password must be different from the current one."); return; }
    setPwSaving(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPwMsg("Password updated successfully. Use the new password on your next sign-in.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (er) { setPwErr(formatApiError(er)); }
    finally { setPwSaving(false); }
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 style={{ fontSize: 28 }}>Settings</h1>
      <p className="vd-muted mb-6">Account information & notification channels.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="vd-card p-6" data-testid="settings-account-card">
          <p className="vd-heading font-semibold mb-3">Signed-in administrator</p>
          <Row k="Name" v={user?.name} />
          <Row k="Email" v={user?.email} />
          <Row k="Role" v={user?.role} />
          <Row k="Account ID" v={user?.id} mono />
        </div>

        <div className="vd-card p-6" data-testid="settings-password-card">
          <p className="vd-heading font-semibold mb-1 inline-flex items-center gap-2"><KeyRound size={16} color="var(--primary-red)" /> Change password</p>
          <p className="vd-muted text-xs mb-4">Use at least 8 characters. You'll stay signed in on this device.</p>
          <form onSubmit={changePassword} className="flex flex-col gap-3">
            <PwField label="Current password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} show={pwShow} testid="pw-current" />
            <PwField label="New password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} show={pwShow} testid="pw-new" />
            <PwField label="Confirm new password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} show={pwShow} testid="pw-confirm" />
            <label className="flex items-center gap-2 text-xs vd-muted cursor-pointer">
              <input type="checkbox" className="vd-checkbox" checked={pwShow} onChange={(e) => setPwShow(e.target.checked)} data-testid="pw-show-toggle" />
              <span className="inline-flex items-center gap-1">{pwShow ? <Eye size={12} /> : <EyeOff size={12} />} Show passwords</span>
            </label>
            {pwMsg && <p className="text-xs" style={{ color: "#00A35C" }} data-testid="pw-msg">{pwMsg}</p>}
            {pwErr && <p className="text-xs" style={{ color: "var(--primary-red)" }} data-testid="pw-err">{pwErr}</p>}
            <button type="submit" disabled={pwSaving} className="vd-btn-primary justify-center text-sm" data-testid="pw-save">
              {pwSaving ? "Updating…" : "Update password"}
            </button>
          </form>
        </div>

        <div className="vd-card p-6" data-testid="settings-notifications-card">
          <div className="flex items-center justify-between mb-3">
            <p className="vd-heading font-semibold">Notification channels</p>
            <button onClick={loadAll} className="vd-btn-secondary text-xs" data-testid="notif-refresh"><RefreshCw size={12} /> Refresh</button>
          </div>

          {loading || !status ? <p className="vd-muted text-sm">Loading…</p> : (
            <div className="flex flex-col gap-3">
              <ChannelRow Icon={Mail} label="Email" provider="Resend" data={status.email} hint={status.email.sender} testid="channel-email" />
              <ChannelRow Icon={MessageSquare} label="SMS" provider="Twilio" data={status.sms} hint={status.sms.from_number} testid="channel-sms" />

              <button onClick={sendTest} disabled={sending} className="vd-btn-primary justify-center mt-2 text-sm" data-testid="notif-test-btn">
                {sending ? "Sending test…" : "Send test notification"}
              </button>
              {msg && <p className="text-xs" style={{ color: "#00A35C" }} data-testid="notif-test-msg">{msg}</p>}
              {err && <p className="text-xs" style={{ color: "var(--primary-red)" }} data-testid="notif-test-err">{err}</p>}
              <p className="vd-muted text-xs">A test fires the most-recent order's "completed" notification through every enabled channel. Disabled channels log a "skipped" entry below.</p>
            </div>
          )}
        </div>
      </div>

      <div className="vd-card p-6 mt-5" data-testid="settings-notifications-log">
        <p className="vd-heading font-semibold mb-3">Recent notification log</p>
        {log.length === 0 ? (
          <p className="vd-muted text-sm">No notifications have been fired yet — they'll appear here as soon as the first order status change triggers one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left vd-muted uppercase text-xs tracking-wider border-b" style={{ borderColor: "var(--border-color)" }}>
                <th className="px-2 py-2">When</th><th>Channel</th><th>Order</th><th>Status</th><th>Recipient</th><th>Detail</th>
              </tr></thead>
              <tbody>
                {log.map((e) => (
                  <tr key={e.id} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`notif-log-${e.id}`}>
                    <td className="px-2 py-2 vd-muted text-xs">{new Date(e.timestamp).toLocaleString()}</td>
                    <td className="vd-heading">{e.channel === "email" ? "Email" : "SMS"}</td>
                    <td className="vd-muted text-xs font-mono">{(e.order_id || "").slice(0, 8)}…</td>
                    <td><LogStatusBadge status={e.status} /></td>
                    <td className="vd-muted text-xs">{e.recipient || "—"}</td>
                    <td className="vd-muted text-xs">{e.reason || (e.provider_id ? `id ${e.provider_id.slice(0, 12)}…` : "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ChannelRow = ({ Icon, label, provider, data, hint, testid }) => {
  const enabled = data?.enabled;
  return (
    <div className="flex items-start justify-between rounded-md p-3" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)" }} data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center rounded-md mt-0.5" style={{ width: 36, height: 36, background: enabled ? "rgba(0,180,80,0.15)" : "rgba(255,179,11,0.18)", color: enabled ? "#00A35C" : "#B57E00" }}>
          <Icon size={18} />
        </span>
        <div>
          <p className="vd-heading font-semibold text-sm">{label} <span className="vd-muted font-normal">· {provider}</span></p>
          {enabled ? (
            <p className="vd-muted text-xs">Enabled{hint ? <> · sending from <span className="font-mono">{hint}</span></> : null}</p>
          ) : (
            <p className="vd-muted text-xs">Disabled — add <span className="font-mono">{(data?.missing_keys || []).join(", ")}</span> to backend .env to activate.</p>
          )}
        </div>
      </div>
      <span className="text-xs font-semibold rounded-full px-2 py-0.5 inline-flex items-center gap-1" style={{ background: enabled ? "rgba(0,180,80,0.15)" : "rgba(255,179,11,0.18)", color: enabled ? "#00A35C" : "#B57E00" }} data-testid={`${testid}-badge`}>
        {enabled ? <><CheckCircle2 size={12} /> Live</> : <><AlertTriangle size={12} /> Off</>}
      </span>
    </div>
  );
};

const LogStatusBadge = ({ status }) => {
  const map = {
    sent:    { bg: "rgba(0,180,80,0.15)",  fg: "#00A35C", Icon: CheckCircle2 },
    skipped: { bg: "rgba(255,179,11,0.18)", fg: "#B57E00", Icon: AlertTriangle },
    failed:  { bg: "rgba(230,0,0,0.15)",   fg: "var(--primary-red)", Icon: XCircle },
  };
  const c = map[status] || { bg: "var(--bg-secondary)", fg: "var(--text-heading)", Icon: CheckCircle2 };
  return <span className="text-xs font-semibold rounded px-2 py-0.5 inline-flex items-center gap-1" style={{ background: c.bg, color: c.fg }}><c.Icon size={11} /> {status}</span>;
};

const Row = ({ k, v, mono }) => (
  <div className="py-2 border-b last:border-0" style={{ borderColor: "var(--border-color)" }}>
    <p className="vd-muted text-xs uppercase tracking-wider">{k}</p>
    <p className={`vd-heading font-medium text-sm ${mono ? "font-mono" : ""}`}>{v || "—"}</p>
  </div>
);

const PwField = ({ label, value, onChange, show, testid }) => (
  <label className="flex flex-col gap-1">
    <span className="vd-muted text-xs uppercase tracking-wider">{label}</span>
    <input
      type={show ? "text" : "password"}
      className="vd-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="new-password"
      required
      minLength={testid === "pw-current" ? undefined : 8}
      data-testid={testid}
    />
  </label>
);

export default AdminSettings;
